type Reservation = {
  covers: Map<unknown, string>;
  inlinePadding: string;
  /** Padding the page already had, kept as a floor under every cover. */
  basePadding: string;
};

const reservations = new WeakMap<HTMLElement, Reservation>();

function toLength(cover: number | string): string {
  if (typeof cover === 'string') return cover.trim();
  const px = Math.max(0, Math.round(cover));
  return px > 0 ? `${px}px` : '';
}

function isEmptyLength(value: string): boolean {
  return !value || value === 'auto' || Number.parseFloat(value) === 0;
}

function apply(
  port: HTMLElement,
  reservation: Reservation,
  exclude?: ReadonlySet<unknown>,
): void {
  const lengths: string[] = [];
  reservation.covers.forEach((length, key) => {
    if (exclude?.has(key) || isEmptyLength(length)) return;
    lengths.push(length);
  });
  if (lengths.length > 0) {
    if (!isEmptyLength(reservation.basePadding)) {
      lengths.unshift(reservation.basePadding);
    }
    port.style.setProperty(
      'scroll-padding-top',
      lengths.length === 1 ? lengths[0] : `max(${lengths.join(', ')})`,
    );
    return;
  }
  if (reservation.inlinePadding) {
    port.style.setProperty('scroll-padding-top', reservation.inlinePadding);
    return;
  }
  port.style.removeProperty('scroll-padding-top');
}

function readPaddingTop(port: HTMLElement): string {
  const view = port.ownerDocument.defaultView;
  return (view && view.getComputedStyle(port).scrollPaddingTop) || '';
}

function toPx(value: string): number {
  if (!value.endsWith('px')) return 0;
  const px = Number.parseFloat(value);
  return Number.isFinite(px) ? Math.max(0, px) : 0;
}

/**
 * The element `scrollIntoView` actually moves. A preview frame scrolls an
 * inner overflow box; a published page scrolls the document.
 */
export function findScrollPort(element: HTMLElement): HTMLElement {
  const doc = element.ownerDocument;
  const view = doc.defaultView;
  if (!view) return doc.documentElement;

  let node = element.parentElement;
  while (node && node !== doc.body && node !== doc.documentElement) {
    const { overflowY } = view.getComputedStyle(node);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      return node;
    }
    node = node.parentElement;
  }

  return (doc.scrollingElement as HTMLElement | null) ?? doc.documentElement;
}

export function isDocumentScroller(port: HTMLElement, doc: Document): boolean {
  return (
    port === doc.documentElement ||
    port === doc.body ||
    port === doc.scrollingElement
  );
}

/** Viewport scrollers report a negative box once the page has moved. */
export function portVisibleTop(port: HTMLElement, doc: Document): number {
  if (isDocumentScroller(port, doc)) return 0;
  return port.getBoundingClientRect().top;
}

/**
 * How far a docked top bar reaches into the visible scrollport once it has
 * finished sliding in. The computed `top` and layout height stay put while a
 * transform is still moving the bar, so the reserved padding is the docked
 * cover rather than a frame from the animation.
 */
export function measureTopCover(bar: HTMLElement, port: HTMLElement): number {
  const view = bar.ownerDocument.defaultView;
  const top = view ? Number.parseFloat(view.getComputedStyle(bar).top) : 0;
  const topOffset = Number.isFinite(top) ? Math.max(0, top) : 0;
  return Math.max(
    0,
    Math.round(
      topOffset + bar.offsetHeight - portVisibleTop(port, bar.ownerDocument),
    ),
  );
}

/**
 * Reserves `scroll-padding-top` on the scrollport so in-page jumps land
 * below everything docked at its top. `cover` is pixels or any CSS length.
 * The tallest reservation wins, never less than the padding the page's own
 * styles already set, and the port's inline padding is restored when every
 * owner has released. Use this rather than writing `scroll-padding-top`
 * directly so docked elements don't overwrite each other.
 */
export function reserveScrollPadding(
  port: HTMLElement,
  key: unknown,
  cover: number | string,
): void {
  let reservation = reservations.get(port);
  if (!reservation) {
    reservation = {
      covers: new Map(),
      inlinePadding: port.style.scrollPaddingTop,
      basePadding: readPaddingTop(port),
    };
    reservations.set(port, reservation);
  }
  reservation.covers.set(key, toLength(cover));
  apply(port, reservation);
}

export function releaseScrollPadding(port: HTMLElement, key: unknown): void {
  const reservation = reservations.get(port);
  if (!reservation) return;
  reservation.covers.delete(key);
  apply(port, reservation);
  if (reservation.covers.size === 0) reservations.delete(port);
}

/**
 * Resolved `scroll-padding-top` of `port` in pixels, leaving out the
 * reservations held under `exclude`.
 */
export function scrollPaddingTop(
  port: HTMLElement,
  exclude: ReadonlySet<unknown> = new Set(),
): number {
  const reservation = reservations.get(port);
  const skips =
    reservation !== undefined &&
    Array.from(reservation.covers.keys()).some((key) => exclude.has(key));
  if (!reservation || !skips) return toPx(readPaddingTop(port));
  apply(port, reservation, exclude);
  const padding = toPx(readPaddingTop(port));
  apply(port, reservation);
  return padding;
}
